import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ImagePlus, Trash2 } from 'lucide-react';
import BlogRichTextEditor from '../../components/admin/BlogRichTextEditor';
import { useBlog } from '../../context/BlogContext';
import { blogService } from '../../services/blog/blogService';
import type { BlogPostInput } from '../../types';
import {
  BLOG_MEDIA_ACCEPT,
  fromDatetimeLocalValue,
  getBlogPostThumbnail,
  hasBlogContent,
  normalizeEditorHtml,
  parseBlogPostId,
  toDatetimeLocalValue,
  validateBlogMediaFile,
} from '../../utils/blog';
import { slugify } from '../../utils/product';

const emptyForm = {
  slug: '',
  title: '',
  excerpt: '',
  coverImage: '',
  content: '',
  author: 'HealthShopLongevity',
  isPublished: false,
  publishedAtLocal: '',
};

export default function AdminBlogPostForm() {
  const { id } = useParams<{ id: string }>();
  const postId = parseBlogPostId(id);
  const isEdit = postId !== null;
  const navigate = useNavigate();
  const slugEditedRef = useRef(false);
  const coverImageInputRef = useRef<HTMLInputElement>(null);
  const { getPostById, loadPost, createPost, updatePost, isLoading } = useBlog();
  const [postReady, setPostReady] = useState(!isEdit);

  useEffect(() => {
    if (!postId) {
      setPostReady(true);
      return;
    }

    if (getPostById(postId)) {
      setPostReady(true);
      return;
    }

    let cancelled = false;
    setPostReady(false);
    void loadPost(postId).finally(() => {
      if (!cancelled) setPostReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [postId, getPostById, loadPost]);

  const resolvedPost = postId ? getPostById(postId) : undefined;

  const initial = useMemo(() => {
    if (!resolvedPost) return emptyForm;
    return {
      slug: resolvedPost.slug,
      title: resolvedPost.title,
      excerpt: resolvedPost.excerpt ?? '',
      coverImage: resolvedPost.coverImage ?? '',
      content: resolvedPost.content,
      author: resolvedPost.author ?? '',
      isPublished: resolvedPost.isPublished,
      publishedAtLocal: toDatetimeLocalValue(resolvedPost.publishedAt),
    };
  }, [resolvedPost]);

  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [coverImageBusy, setCoverImageBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setForm(initial);
    slugEditedRef.current = Boolean(resolvedPost?.slug);
  }, [initial, resolvedPost?.slug]);

  if (isEdit && (!postReady || (isLoading && !resolvedPost))) {
    return (
      <div className="admin-page">
        <p>Yükleniyor...</p>
      </div>
    );
  }

  if (isEdit && postReady && !resolvedPost) {
    return (
      <div className="admin-page">
        <p>Blog yazısı bulunamadı.</p>
        <Link to="/admin/blog" className="back-link">
          <ArrowLeft size={16} aria-hidden />
          Bloga Dön
        </Link>
      </div>
    );
  }

  const updateField = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'title' && !slugEditedRef.current) {
        next.slug = slugify(String(value));
      }
      return next;
    });
  };

  const updateSlug = (value: string) => {
    slugEditedRef.current = true;
    setForm((prev) => ({ ...prev, slug: value }));
  };

  const buildPayload = (): BlogPostInput => ({
    slug: form.slug.trim() || null,
    title: form.title.trim(),
    excerpt: form.excerpt.trim() || null,
    coverImage: form.coverImage.trim() || null,
    content: normalizeEditorHtml(form.content.trim()),
    author: form.author.trim() || null,
    isPublished: form.isPublished,
    publishedAt: fromDatetimeLocalValue(form.publishedAtLocal),
  });

  const coverPreview = getBlogPostThumbnail({
    coverImage: form.coverImage,
    content: form.content,
  });

  const handleCoverImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const validationError = validateBlogMediaFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setCoverImageBusy(true);
    setError('');

    try {
      const uploadedPath = await blogService.uploadMedia(file);
      updateField('coverImage', uploadedPath);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kapak görseli yüklenemedi.');
    } finally {
      setCoverImageBusy(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    const normalizedContent = normalizeEditorHtml(form.content);
    if (!hasBlogContent(normalizedContent)) {
      setError('Blog içeriği zorunludur.');
      return;
    }

    setLoading(true);

    try {
      if (isEdit && postId) {
        await updatePost(postId, buildPayload());
      } else {
        await createPost(buildPayload());
      }
      navigate('/admin/blog');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kayıt başarısız.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <Link to="/admin/blog" className="back-link">
            <ArrowLeft size={16} aria-hidden />
            Bloga Dön
          </Link>
          <h1>{isEdit ? 'Yazıyı Düzenle' : 'Yeni Blog Yazısı'}</h1>
        </div>
      </div>

      {error && <div className="auth-error">{error}</div>}

      <form className="admin-form" onSubmit={handleSubmit}>
        <section className="admin-form-section">
          <h2>İçerik</h2>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="title">Başlık *</label>
              <input
                id="title"
                value={form.title}
                onChange={(e) => updateField('title', e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="slug">SEO Slug</label>
              <input
                id="slug"
                value={form.slug}
                onChange={(e) => updateSlug(e.target.value)}
                placeholder="tdcs-nedir"
              />
              <span className="form-hint">URL: /blog/{form.slug || '...'}</span>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="excerpt">Özet</label>
            <textarea
              id="excerpt"
              rows={3}
              value={form.excerpt}
              onChange={(e) => updateField('excerpt', e.target.value)}
              placeholder="Liste ve SEO için kısa açıklama"
            />
          </div>

          <div className="form-group">
            <label>Kapak görseli</label>
            <input
              ref={coverImageInputRef}
              type="file"
              accept={BLOG_MEDIA_ACCEPT}
              hidden
              onChange={(event) => void handleCoverImageSelect(event)}
            />
            <div className="blog-cover-image-field">
              {coverPreview ? (
                <img
                  src={coverPreview}
                  alt="Kapak görseli önizlemesi"
                  className="blog-cover-image-preview"
                />
              ) : (
                <div className="blog-cover-image-preview blog-cover-image-preview-empty">
                  Liste kartında görünecek küçük resim
                </div>
              )}
              <div className="blog-cover-image-actions">
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  disabled={coverImageBusy}
                  onClick={() => coverImageInputRef.current?.click()}
                >
                  <ImagePlus size={16} aria-hidden />
                  {coverImageBusy ? 'Yükleniyor...' : 'Görsel Yükle'}
                </button>
                {form.coverImage && (
                  <button
                    type="button"
                    className="btn btn-outline btn-sm"
                    onClick={() => updateField('coverImage', '')}
                  >
                    <Trash2 size={16} aria-hidden />
                    Kaldır
                  </button>
                )}
              </div>
            </div>
            <span className="form-hint">
              Boş bırakılırsa içerikteki ilk görsel kullanılır. Görsel yoksa varsayılan kart görünür.
            </span>
          </div>

          <div className="form-group">
            <label htmlFor="content">İçerik *</label>
            <BlogRichTextEditor
              id="content"
              value={form.content}
              onChange={(html) => updateField('content', html)}
              placeholder="Başlıklar, listeler ve bağlantılar ekleyebilirsiniz."
            />
            <span className="form-hint">
              Metin biçimlendirme, görsel yükleme, YouTube videosu ve ayırıcı çizgi ekleyebilirsiniz.
            </span>
          </div>

          <div className="form-group">
            <label htmlFor="author">Yazar</label>
            <input
              id="author"
              value={form.author}
              onChange={(e) => updateField('author', e.target.value)}
              placeholder="HealthShopLongevity"
            />
          </div>
        </section>

        <section className="admin-form-section">
          <h2>Yayın</h2>

          <label className="admin-checkbox">
            <input
              type="checkbox"
              checked={form.isPublished}
              onChange={(e) => updateField('isPublished', e.target.checked)}
            />
            Yayında göster
          </label>

          <div className="form-group">
            <label htmlFor="publishedAt">Yayın tarihi</label>
            <input
              id="publishedAt"
              type="datetime-local"
              value={form.publishedAtLocal}
              onChange={(e) => updateField('publishedAtLocal', e.target.value)}
            />
            <span className="form-hint">Boş bırakılırsa yayına alındığında otomatik atanır.</span>
          </div>
        </section>

        <div className="admin-form-actions">
          <Link to="/admin/blog" className="btn btn-outline">
            İptal
          </Link>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Kaydediliyor...' : isEdit ? 'Güncelle' : 'Yazı Ekle'}
          </button>
        </div>
      </form>
    </div>
  );
}
